import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Phone, Mail, MapPin, X, Home, UserPlus, KeyRound, Smartphone } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";

export default function Login() {
  usePageTitle("Sign In");
  const { login, setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next") || "";
  const [mode, setMode] = useState("password"); // "password" | "otp"
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // OTP login state
  const [otpPhone, setOtpPhone] = useState("");
  const [otpStep, setOtpStep] = useState(1); // 1 = phone, 2 = code
  const [otpCode, setOtpCode] = useState("");
  const [otpName, setOtpName] = useState("");
  const [otpErr, setOtpErr] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = enter id, 2 = enter OTP + new password
  const [forgotId, setForgotId] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPwd, setForgotNewPwd] = useState("");
  const [forgotMaskedTo, setForgotMaskedTo] = useState("");
  const [forgotErr, setForgotErr] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  const resetForgot = () => {
    setShowForgot(false); setForgotStep(1); setForgotId(""); setForgotOtp("");
    setForgotNewPwd(""); setForgotMaskedTo(""); setForgotErr(""); setForgotDone(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(identifier, password);
      const dest = nextParam || (u.role === "customer" ? "/portal" : "/dashboard");
      navigate(dest);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotErr("");
    setForgotBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier: forgotId });
      setForgotMaskedTo(data.masked_to || "your registered mobile");
      setForgotStep(2);
    } catch (e2) {
      setForgotErr(formatApiError(e2));
    } finally { setForgotBusy(false); }
  };

  const verifyForgot = async (e) => {
    e.preventDefault();
    setForgotErr("");
    setForgotBusy(true);
    try {
      await api.post("/auth/verify-otp-reset", {
        identifier: forgotId, code: forgotOtp, new_password: forgotNewPwd,
      });
      setForgotDone(true);
    } catch (e2) {
      setForgotErr(formatApiError(e2));
    } finally { setForgotBusy(false); }
  };

  const submitOtpSend = async (e) => {
    e.preventDefault();
    setOtpErr(""); setOtpBusy(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone: otpPhone });
      setOtpSentTo(data.phone);
      setOtpStep(2);
      setOtpCooldown(30);
      const iv = setInterval(() => {
        setOtpCooldown((s) => {
          if (s <= 1) { clearInterval(iv); return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (e2) {
      setOtpErr(formatApiError(e2));
    } finally { setOtpBusy(false); }
  };

  const submitOtpVerify = async (e) => {
    e.preventDefault();
    setOtpErr(""); setOtpBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        phone: otpSentTo || otpPhone, otp: otpCode, name: otpName || undefined,
      });
      setSession(data.token, data.user);
      const dest = nextParam || (data.user.role === "customer" ? "/portal" : "/dashboard");
      navigate(dest);
    } catch (e2) {
      setOtpErr(formatApiError(e2));
    } finally { setOtpBusy(false); }
  };

  const submitOtpResend = async () => {
    if (otpCooldown > 0 || otpBusy) return;
    setOtpErr(""); setOtpBusy(true);
    try {
      await api.post("/auth/otp/resend", { phone: otpSentTo });
      setOtpCooldown(30);
      const iv = setInterval(() => {
        setOtpCooldown((s) => {
          if (s <= 1) { clearInterval(iv); return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (e2) {
      setOtpErr(formatApiError(e2));
    } finally { setOtpBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[hsl(var(--background))]">
      {/* Left */}
      <div className="flex flex-col justify-between p-10 lg:p-16 order-2 lg:order-1">
        <div className="flex items-center justify-between">
          <Logo height={56} variant="light" />
          <div className="flex items-center gap-2">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 border-2 border-zinc-200 rounded-full hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors" data-testid="login-home-btn">
              <Home size={12} /> Home
            </Link>
            <Link to={`/signup${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ""}`} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 bg-[#FF6B00] text-white rounded-full hover:bg-[#e65a00] transition-colors" data-testid="login-signup-btn">
              <UserPlus size={12} /> Sign Up
            </Link>
          </div>
        </div>

        <div className="max-w-md">
          <div className="overline mb-4">Order Management System</div>
          <h1 className="font-display text-5xl lg:text-6xl font-black leading-[0.95] tracking-tighter mb-4">
            Precision.
            <br />
            <span className="text-[#FF6B00]">Every</span> print.
          </h1>
          <p className="text-muted-foreground mb-8">
            Sign in to manage customers, orders and production for Brothers Advertising.
          </p>

          {/* Mode tabs */}
          <div className="inline-flex rounded-full border-2 border-zinc-200 p-1 mb-6" data-testid="login-mode-tabs">
            <button
              type="button"
              onClick={() => { setMode("password"); setErr(""); }}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${mode === "password" ? "bg-[#FF6B00] text-white" : "text-zinc-600 hover:text-zinc-900"}`}
              data-testid="login-mode-password"
            >
              <KeyRound size={12} /> Password
            </button>
            <button
              type="button"
              onClick={() => { setMode("otp"); setOtpErr(""); }}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${mode === "otp" ? "bg-[#FF6B00] text-white" : "text-zinc-600 hover:text-zinc-900"}`}
              data-testid="login-mode-otp"
            >
              <Smartphone size={12} /> OTP
            </button>
          </div>

          {mode === "otp" && otpStep === 1 && (
            <form onSubmit={submitOtpSend} className="space-y-4" data-testid="otp-send-form">
              <div>
                <label className="overline block mb-2">Mobile Number</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 border-[hsl(var(--border))] bg-zinc-50 text-sm font-mono text-zinc-600">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={otpPhone}
                    onChange={(e) => setOtpPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="10-digit mobile"
                    required
                    minLength={10}
                    className="flex-1 border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5 outline-none focus:border-[#FF6B00]"
                    data-testid="otp-phone-input"
                  />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">We'll send a 6-digit code by SMS.</div>
              </div>
              {otpErr && (
                <div className="text-sm text-red-600 border border-red-300 bg-red-50 px-3 py-2" data-testid="otp-send-error">{otpErr}</div>
              )}
              <button type="submit" disabled={otpBusy || otpPhone.length !== 10}
                      className="btn-swiss btn-accent w-full justify-center py-3" data-testid="otp-send-btn">
                {otpBusy ? "Sending…" : "Send OTP →"}
              </button>
            </form>
          )}

          {mode === "otp" && otpStep === 2 && (
            <form onSubmit={submitOtpVerify} className="space-y-4" data-testid="otp-verify-form">
              <div className="text-sm">
                Code sent to <b className="font-mono">+{otpSentTo}</b>{" "}
                <button type="button" onClick={() => { setOtpStep(1); setOtpCode(""); }}
                        className="text-[#FF6B00] hover:underline text-xs" data-testid="otp-change-number">
                  change
                </button>
              </div>
              <div>
                <label className="overline block mb-2">Enter OTP</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit code"
                  autoFocus
                  required
                  minLength={4}
                  className="w-full text-center text-2xl font-mono tracking-[0.5em] border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-3 outline-none focus:border-[#FF6B00]"
                  data-testid="otp-code-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">Your name <span className="normal-case text-muted-foreground">(only for new customers)</span></label>
                <input
                  type="text"
                  value={otpName}
                  onChange={(e) => setOtpName(e.target.value)}
                  placeholder="Optional"
                  className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5 outline-none focus:border-[#FF6B00]"
                  data-testid="otp-name-input"
                />
              </div>
              {otpErr && (
                <div className="text-sm text-red-600 border border-red-300 bg-red-50 px-3 py-2" data-testid="otp-verify-error">{otpErr}</div>
              )}
              <div className="flex items-center justify-between">
                <button type="button" onClick={submitOtpResend} disabled={otpCooldown > 0 || otpBusy}
                        className="text-xs text-[#FF6B00] hover:underline disabled:text-zinc-400 disabled:no-underline" data-testid="otp-resend-btn">
                  {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Resend OTP"}
                </button>
                <button type="submit" disabled={otpBusy || otpCode.length < 4}
                        className="btn-swiss btn-accent px-6 py-3" data-testid="otp-verify-btn">
                  {otpBusy ? "Verifying…" : "Verify & Sign in →"}
                </button>
              </div>
            </form>
          )}

          {mode === "password" && (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="overline block mb-2">Email or Mobile</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                data-testid="login-email-input"
                className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5 outline-none focus:border-[#FF6B00]"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5 outline-none focus:border-[#FF6B00]"
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-remember-checkbox"
                />
                Remember me
              </label>
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                data-testid="forgot-password-btn"
                className="text-muted-foreground hover:text-[#FF6B00]"
              >
                Forgot password?
              </button>
            </div>

            {err && (
              <div className="text-sm text-red-600 border border-red-300 bg-red-50 dark:bg-red-950/40 px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-swiss btn-accent w-full justify-center py-3"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign in →"}
            </button>
          </form>
          )}

          {/* Google sign-in for customers */}
          <div className="mt-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-[hsl(var(--border))]" />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Customers</div>
              <div className="flex-1 h-px bg-[hsl(var(--border))]" />
            </div>
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/portal";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 border-2 border-[hsl(var(--border))] hover:border-[#FF6B00] bg-white text-black px-4 py-2.5 font-semibold"
              data-testid="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C41.3 35.8 44 30.4 44 24c0-1.3-.1-2.6-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>
            <p className="mt-3 text-xs text-center text-muted-foreground">
              New customer? Sign in with Google — we'll set up your portal automatically.
            </p>
          </div>

          {/* Contact block */}
          <div className="mt-8 border-t border-[hsl(var(--border))] pt-6 space-y-2 text-sm" data-testid="contact-details">
            <div className="overline mb-3">Contact Brothers Advertising</div>
            <div className="flex items-start gap-3">
              <Phone size={14} className="text-[#FF6B00] mt-1" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Customer Care</div>
                <a href="tel:02247427059" className="hover:text-[#FF6B00] font-mono">022 4742 7059</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone size={14} className="text-[#FF6B00] mt-1" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Mobile / WhatsApp</div>
                <a href="tel:918070970980" className="hover:text-[#FF6B00] font-mono">+91 80709 70980</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={14} className="text-[#FF6B00] mt-1" />
              <a href="mailto:print@brothersdigital.in" className="hover:text-[#FF6B00]">print@brothersdigital.in</a>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={14} className="text-[#FF6B00] mt-1 flex-shrink-0" />
              <span className="text-muted-foreground">
                Shop No. 02, Payal Residency, Plot No. 29, Sector 16, Taloja Phase 2, Navi Mumbai — 410208
              </span>
            </div>
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          © 2026 Brothers Advertising · Signage · Branding · Print
        </div>
      </div>

      {/* Right image */}
      <div
        className="hidden lg:block relative order-1 lg:order-2"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1503694978374-8a2fa686963a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwcHJpbnRpbmclMjBwcmVzcyUyMG1hY2hpbmUlMjBvZmZzZXR8ZW58MHx8fHwxNzgyODkzMDEyfDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="overline text-white/70 mb-3">Est. 2019 · Navi Mumbai</div>
          <div className="font-display text-3xl font-bold leading-tight max-w-md">
            From flex to acrylic — we run the press so your brand never sleeps.
          </div>
        </div>
      </div>

      {/* Forgot Password Modal — OTP flow */}
      {showForgot && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="forgot-modal">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] max-w-md w-full">
            <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--border))]">
              <div className="font-display text-xl font-bold">
                {forgotDone ? "Password updated" : forgotStep === 1 ? "Reset Password" : "Enter OTP"}
              </div>
              <button onClick={resetForgot} data-testid="close-forgot-modal">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              {forgotDone ? (
                <div className="space-y-4 text-center" data-testid="forgot-done">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto text-2xl">✓</div>
                  <div className="text-sm">Your password has been updated. Sign in below.</div>
                  <button onClick={() => { setIdentifier(forgotId); setPassword(""); resetForgot(); }}
                    className="btn-swiss btn-accent w-full justify-center" data-testid="forgot-continue-btn">
                    Sign in now
                  </button>
                </div>
              ) : forgotStep === 1 ? (
                <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-step-1">
                  <p className="text-sm text-muted-foreground">
                    Enter your registered mobile number. We'll SMS a 6-digit code to reset your password.
                  </p>
                  <input required value={forgotId} onChange={(e) => setForgotId(e.target.value)}
                    placeholder="Mobile number (or email)"
                    className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
                    data-testid="forgot-identifier-input" />
                  {forgotErr && <div className="text-sm text-red-600" data-testid="forgot-error">{forgotErr}</div>}
                  <button type="submit" disabled={forgotBusy}
                    className="btn-swiss btn-accent w-full justify-center" data-testid="forgot-submit-btn">
                    {forgotBusy ? "Sending…" : "Send OTP"}
                  </button>
                </form>
              ) : (
                <form onSubmit={verifyForgot} className="space-y-4" data-testid="forgot-step-2">
                  <p className="text-sm">
                    We sent a 6-digit code to <span className="font-mono font-bold">{forgotMaskedTo}</span>. Enter it below with your new password.
                  </p>
                  <input required maxLength={6} inputMode="numeric" pattern="[0-9]{6}"
                    value={forgotOtp} onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="6-digit code"
                    className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-3 text-lg font-mono tracking-[0.4em] text-center outline-none focus:border-[#FF6B00]"
                    data-testid="forgot-otp-input" />
                  <input required type="password" minLength={6}
                    value={forgotNewPwd} onChange={(e) => setForgotNewPwd(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
                    data-testid="forgot-newpwd-input" />
                  {forgotErr && <div className="text-sm text-red-600" data-testid="forgot-error">{forgotErr}</div>}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setForgotStep(1); setForgotOtp(""); setForgotErr(""); }}
                      className="btn-swiss btn-outline" data-testid="forgot-back-btn">Back</button>
                    <button type="submit" disabled={forgotBusy || forgotOtp.length !== 6 || forgotNewPwd.length < 6}
                      className="btn-swiss btn-accent flex-1 justify-center" data-testid="forgot-verify-btn">
                      {forgotBusy ? "Verifying…" : "Update Password"}
                    </button>
                  </div>
                  <button type="button" onClick={submitForgot} disabled={forgotBusy}
                    className="text-xs text-[#FF6B00] hover:underline" data-testid="forgot-resend-btn">
                    Didn't get the SMS? Resend
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
